/** Move-to-folder menu — select destination folder for a personal lesson using learner UI primitives. */
import { FolderOpen, Check } from 'lucide-react';
import { Button as AriaButton } from 'react-aria-components';
import { toast } from 'sonner';
import { MenuTrigger, Menu, MenuItem } from '@/interfaces/learner/ui';
import { useFolders, useMoveToFolder } from '@/shared/api/hooks';

interface MoveToFolderMenuProps {
  lessonId: string;
  currentFolderId: string | null;
}

export function MoveToFolderMenu({ lessonId, currentFolderId }: MoveToFolderMenuProps) {
  const { data: folders = [] } = useFolders();
  const moveToFolder = useMoveToFolder();

  const handleSelect = (folderId: string | null) => {
    if (folderId === currentFolderId) return;
    moveToFolder.mutate(
      { lessonId, folderId },
      {
        onError: (err) => toast.error(err.message || 'Failed to move lesson'),
      }
    );
  };

  const checkIcon = <Check size={13} strokeWidth={2.5} />;

  return (
    <MenuTrigger>
      <AriaButton
        aria-label="Move to folder"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: 'var(--control-radius-sm)',
          background: 'transparent',
          border: 'none',
          color: 'var(--color-text-muted)',
          cursor: 'pointer',
          transition: 'background 0.15s',
        }}
        className="hover:bg-[var(--color-surface-muted)]"
      >
        <FolderOpen size={14} strokeWidth={1.75} />
      </AriaButton>
      <Menu placement="bottom end">
        {/* Unfiled */}
        <MenuItem
          icon={currentFolderId === null ? checkIcon : undefined}
          onAction={() => handleSelect(null)}
        >
          Unfiled
        </MenuItem>

        {/* Folders */}
        {folders.map((f) => (
          <MenuItem
            key={f.id}
            icon={currentFolderId === f.id ? checkIcon : undefined}
            onAction={() => handleSelect(f.id)}
          >
            {f.name}
          </MenuItem>
        ))}
      </Menu>
    </MenuTrigger>
  );
}
